import { useState, useEffect, useRef } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { Menu, X, Instagram, Mail, MapPin, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [donateDropdownOpen, setDonateDropdownOpen] = useState(false);
  const [mobileDonateOpen, setMobileDonateOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setDonateDropdownOpen(false);
    setMobileDonateOpen(false);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDonateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navLinksBeforeDonate = [
    { name: "About", path: "/about" },
    { name: "Our Pets", path: "/our-pets" },
    { name: "Applications", path: "/applications" },
    { name: "Store", path: "/store" },
    { name: "Contact", path: "/contact" },
  ];

  const navLinksAfterDonate = [
    { name: "Resources", path: "/resources" },
  ];

  const donateLinks = [
    { name: "Donate to Us", path: "/donate" },
    { name: "Danger Zone", path: "/danger-zone" },
    { name: "Amazon Wishlist", path: "/amazon-wishlist" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path.split("#")[0]);
  };

  const isDonateActive = () => {
    return location.pathname === "/donate" || location.pathname === "/danger-zone" || location.pathname === "/amazon-wishlist";
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
        isScrolled ? "bg-[#0A0A0A] border-b border-white/10" : "bg-[#0A0A0A]/95"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="logo-link"
            className="flex items-center space-x-3 group"
          >
            <img 
              src="/logo.png" 
              alt="3 Eye Blind Rescue Logo" 
              className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
            />
            <div>
              <span className="font-['Bebas_Neue'] text-base sm:text-xl tracking-tight text-white group-hover:text-[#FFD700] transition-colors">
                THREE EYE BLIND
              </span>
              <span className="block text-[10px] sm:text-xs text-[#A3A3A3] uppercase tracking-widest font-mono">
                Animal Rescue
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center">
            {navLinksBeforeDonate.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-3 font-mono text-sm uppercase tracking-widest transition-colors h-20 flex items-center ${
                  isActive(link.path)
                    ? "text-[#FFD700]"
                    : "text-white hover:text-[#FFD700]"
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Donate Dropdown */}
            <div className="relative z-[150]" ref={dropdownRef}>
              <button
                onClick={() => setDonateDropdownOpen(!donateDropdownOpen)}
                data-testid="nav-donate-dropdown"
                className={`px-3 font-mono text-sm uppercase tracking-widest transition-colors h-20 flex items-center gap-1 ${
                  isDonateActive()
                    ? "text-[#FFD700]"
                    : "text-white hover:text-[#FFD700]"
                }`}
              >
                Donate
                <ChevronDown className={`w-4 h-4 transition-transform ${donateDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {donateDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-0 bg-[#0A0A0A] border border-[#4A5320] rounded-md shadow-lg min-w-[180px] overflow-hidden z-[200]"
                  >
                    {donateLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                        className={`block px-4 py-3 font-mono text-sm uppercase tracking-widest transition-colors whitespace-nowrap ${
                          isActive(link.path)
                            ? "text-[#FFD700] bg-[#4A5320]/20"
                            : "text-white hover:text-[#FFD700] hover:bg-[#4A5320]/10"
                        }`}
                        onClick={() => setDonateDropdownOpen(false)}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Resources link after Donate */}
            {navLinksAfterDonate.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-3 font-mono text-sm uppercase tracking-widest transition-colors h-20 flex items-center ${
                  isActive(link.path)
                    ? "text-[#FFD700]"
                    : "text-white hover:text-[#FFD700]"
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* CTA Button */}
            <Link
              to="/donate"
              data-testid="nav-donate-cta"
              className="ml-4 btn-primary text-sm"
            >
              Donate Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-white hover:text-[#FFD700] transition-colors"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#0A0A0A] border-t border-[#4A5320]"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinksBeforeDonate.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`block py-2 font-mono text-sm uppercase tracking-widest ${
                    isActive(link.path) ? "text-[#FFD700]" : "text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Donate Dropdown */}
              <div>
                <button
                  onClick={() => setMobileDonateOpen(!mobileDonateOpen)}
                  className={`flex items-center justify-between w-full py-2 font-mono text-sm uppercase tracking-widest ${
                    isDonateActive() ? "text-[#FFD700]" : "text-white"
                  }`}
                >
                  Donate
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileDonateOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {mobileDonateOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="pl-4 mt-2 space-y-2 border-l-2 border-[#4A5320]"
                    >
                      {donateLinks.map((link) => (
                        <Link
                          key={link.name}
                          to={link.path}
                          data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                          className={`block py-2 font-mono text-sm uppercase tracking-widest ${
                            isActive(link.path) ? "text-[#FFD700]" : "text-[#A3A3A3]"
                          }`}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Resources after Donate */}
              {navLinksAfterDonate.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`block py-2 font-mono text-sm uppercase tracking-widest ${
                    isActive(link.path) ? "text-[#FFD700]" : "text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              <Link
                to="/donate"
                data-testid="mobile-donate-cta"
                className="block btn-primary text-center text-sm mt-6"
              >
                Donate Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-[#0A0A0A] border-t border-[#4A5320]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo.png" 
                  alt="3 Eye Blind Rescue Logo" 
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <span className="font-['Bebas_Neue'] text-xl tracking-tight text-white">
                    THREE EYE BLIND
                  </span>
                  <span className="block text-xs text-[#A3A3A3] uppercase tracking-widest font-mono">
                    Animal Rescue
                  </span>
                </div>
              </div>
            </Link>
            <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
              A 501(c)(3) nonprofit dedicated to saving at-risk dogs and cats from high-kill shelters.
            </p>
            <p className="text-[#A3A3A3] text-sm font-mono">
              EIN: <span className="text-white">99-0978241</span>
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Bebas_Neue'] text-lg text-[#FFD700] mb-6 tracking-wide">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "About Us", path: "/about" },
                { name: "Our Pets", path: "/our-pets" },
                { name: "Applications", path: "/applications" },
                { name: "Foster Resources", path: "/foster-resources" },
                { name: "Donate", path: "/donate" },
                { name: "Store", path: "/store" },
              ].map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Bebas_Neue'] text-lg text-[#FFD700] mb-6 tracking-wide">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#4A5320] flex-shrink-0 mt-0.5" />
                <a 
                  href="mailto:info@3eyeblindrescue.org"
                  className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors text-sm break-all"
                >
                  info@3eyeblindrescue.org
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#4A5320] flex-shrink-0 mt-0.5" />
                <span className="text-[#A3A3A3] text-sm">
                  4010 Foothills Blvd 103-189<br />
                  Roseville, CA 95747
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Instagram className="w-5 h-5 text-[#4A5320] flex-shrink-0 mt-0.5" />
                <a 
                  href="https://www.instagram.com/3eyeblindrescue/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#A3A3A3] hover:text-[#FFD700] transition-colors text-sm"
                >
                  @3eyeblindrescue
                </a>
              </li>
            </ul>
          </div>

          {/* Support Us */}
          <div>
            <h4 className="font-['Bebas_Neue'] text-lg text-[#FFD700] mb-6 tracking-wide">Support Us</h4>
            <p className="text-[#A3A3A3] text-sm mb-6">
              Every donation helps us save more lives. 100% of proceeds go directly to animal care.
            </p>
            <Link
              to="/donate"
              data-testid="footer-donate-button"
              className="btn-primary inline-block text-sm"
            >
              Donate Now
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#4A5320]">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-[#A3A3A3] text-xs font-mono">
              &copy; {currentYear} Three Eye Blind Animal Rescue. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-[#A3A3A3] text-xs font-mono uppercase tracking-widest">
                US Navy Veteran-Founded
              </span>
              <span className="text-[#4A5320]">|</span>
              <span className="text-[#A3A3A3] text-xs font-mono uppercase tracking-widest">
                Woman-Run
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col noise-bg">
      <Navigation />
      <main className="flex-grow pt-20 relative z-0">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
